import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Logo } from "./Logo";
import { NEXT_FEATURES } from "../featureFlags";
import {
  ME,
  ORGS,
  MY_MEMBERSHIPS,
  GLOBAL_ORG_ID,
  planLabel,
  Org,
  OrgRole,
  ROLE_LABEL,
  isOrgManager,
} from "../mockData";
import {
  Home,
  Film,
  Trophy,
  TrendingUp,
  Boxes,
  Users,
  FileBarChart,
  Settings as SettingsIcon,
  Search,
  Bell,
  Upload as UploadIcon,
  Globe2,
  Building2,
  Check,
  ChevronDown,
  Plus,
  Sparkles,
  Menu,
  X,
  Shield,
} from "lucide-react";
import { SubscriptionLevel, subscriptionLevelForEmail } from "../subscription";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  hint?: string;
};
type NavSection = { title: string; pro?: boolean; items: NavItem[] };

function buildSections(role: OrgRole, isGlobal: boolean, _org: Org, isAdmin: boolean): NavSection[] {
  const rosterHint = isOrgManager(role)
    ? "Athletes, clients, employees"
    : isGlobal
    ? "Friends + people you follow"
    : "Friends + teammates";

  const sections: NavSection[] = [
    {
      title: "Explore",
      items: [
        { to: "/",               label: "Home",      icon: Home,       hint: "Your day at a glance" },
        ...(NEXT_FEATURES
          ? [
              { to: "/explore/feed",   label: "Feed",   icon: Sparkles,   hint: "Picked for you" } as NavItem,
              { to: "/explore/trends", label: "Trends", icon: TrendingUp, hint: "Top challenges" } as NavItem,
            ]
          : []),
        { to: "/explore/search", label: "Search",    icon: Search,     hint: "Find anything" },
      ],
    },
    {
      title: "Activity",
      items: [
        { to: "/sessions", label: "Sessions", icon: Film,       hint: "All captures" },
        // Rankings are visible to ALL users - the global biomechanics database's public face.
        { to: "/rankings", label: "Rankings", icon: Trophy,     hint: "Global athlete database" },
        { to: "/progress", label: "Progress", icon: TrendingUp, hint: "Your stats" },
        ...(NEXT_FEATURES
          ? [{ to: "/sports", label: "Movement", icon: Boxes, hint: "Library + drills" } as NavItem]
          : []),
      ],
    },
    ...(NEXT_FEATURES
      ? [
          {
            title: "Network",
            items: [
              { to: "/roster", label: "Roster", icon: Users, hint: rosterHint } as NavItem,
            ],
          },
        ]
      : []),
  ];

  if (isOrgManager(role) && !isGlobal) {
    sections.push({
      title: "Organisation",
      items: [
        { to: "/reports", label: "AI reports", icon: FileBarChart, hint: "Auto summaries — performance + risk" },
      ],
    });
  }

  if (isOrgManager(role) && !isGlobal) {
    sections.push({
      title: "Setup",
      items: [
        { to: "/settings", label: "Org settings", icon: SettingsIcon, hint: "General, members, billing" },
      ],
    });
  }

  // Internal tooling — hidden from everyone else. The route redirects and the
  // endpoints refuse non-admins regardless; this just keeps the nav honest.
  if (isAdmin) {
    sections.push({
      title: "Admin",
      items: [
        { to: "/admin", label: "Internal tools", icon: Shield, hint: "Eleetz staff only" },
      ],
    });
  }

  return sections;
}

function buildTitleMap(role: OrgRole, isGlobal: boolean, _org: Org, firstName: string): Record<string, { title: string; sub?: string }> {
  const rosterSub = isOrgManager(role)
    ? "Athletes, clients, employees"
    : isGlobal ? "Friends + people you follow" : "Friends + teammates";

  return {
    "/":             { title: `Hi ${firstName}`, sub: "Your training, today" },
    "/sessions":     { title: "Sessions",        sub: "Every capture in this organisation" },
    "/progress":     { title: "Progress",        sub: "Trend, PRs and phase deltas" },
    "/sports":       { title: "Library",         sub: "Movements + drills + ergonomic assessments" },
    "/roster":       { title: "Roster",          sub: rosterSub },
    "/explore":        { title: "Explore",       sub: "Picked for you" },
    "/explore/feed":   { title: "Feed",          sub: "Picked for you" },
    "/explore/trends": { title: "Trends",        sub: "Top challenges" },
    "/explore/search": { title: "Search",        sub: "Find anything" },
    "/reports":      { title: "AI reports",      sub: "Auto-generated summaries — performance + risk" },
    "/profile":      { title: "Profile",         sub: "Your account, privacy, AI, integrations, organisations" },
    "/settings":     { title: "Org settings",    sub: "Owner / coach controls for the active organisation" },
    "/admin":        { title: "Admin",           sub: "Internal tools — Eleetz staff only" },
  };
}

const ORG_KEY = "eleetz.org";

export default function AppShell() {
  const loc = useLocation();
  // Greet the signed-in user, not the mock persona (same precedence as Home's banner).
  const { user } = useAuth0();
  const firstName = (user?.given_name ?? user?.name ?? ME.name).toString().split(" ")[0];

  // Mobile nav drawer. The sidebar is a static column on lg+, but slides in
  // over the content (with a backdrop) on phones/tablets. Auto-close on any
  // route change so tapping a link dismisses the drawer.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [loc.pathname]);

  const myMemberships = MY_MEMBERSHIPS
    .map((m) => ({ org: ORGS.find((o) => o.id === m.orgId)!, role: m.role }))
    .filter((m) => m.org);

  const [orgId, setOrgId] = useState<string>(() => {
    if (typeof window === "undefined") return GLOBAL_ORG_ID;
    const saved = window.localStorage.getItem(ORG_KEY);
    return saved && myMemberships.some((m) => m.org.id === saved) ? saved : GLOBAL_ORG_ID;
  });

  useEffect(() => { window.localStorage.setItem(ORG_KEY, orgId); }, [orgId]);

  const current = myMemberships.find((m) => m.org.id === orgId) ?? myMemberships[0];
  const currentOrg = current.org;
  const role = current.role;
  const isGlobal = currentOrg.id === GLOBAL_ORG_ID;

  const titleMap = buildTitleMap(role, isGlobal, currentOrg, firstName);
  const isAdmin = subscriptionLevelForEmail(user?.email) === SubscriptionLevel.ADMIN;
  const sections = buildSections(role, isGlobal, currentOrg, isAdmin);
  const head =
    titleMap[loc.pathname] ??
    (loc.pathname.startsWith("/sessions/")
      ? { title: "Session", sub: "Frame-by-frame breakdown" }
      : loc.pathname.startsWith("/roster/")
      ? { title: "Profile", sub: "Trend, sessions, body measures" }
      : loc.pathname.startsWith("/explore/trend/")
      ? { title: "Trend", sub: "Global leaderboard + choreography" }
      : { title: "Eleetz" });

  return (
    <div className="min-h-screen flex bg-surface-muted text-slate-800">
      {/* Backdrop — only when the mobile drawer is open */}
      {navOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar — static column on lg+, off-canvas drawer below it */}
      <aside
        className={[
          "fixed inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col",
          "transform transition-transform duration-200 ease-out",
          "lg:static lg:z-auto lg:translate-x-0",
          navOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <div className="h-16 px-5 border-b border-slate-200 flex items-center justify-between">
          <Logo />
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            className="p-1.5 -mr-1.5 rounded-md text-slate-500 hover:bg-slate-100 lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
          {sections.map((section) => (
            <div key={section.title}>
              <div className="px-2 mb-2 flex items-center gap-1.5">
                <span className="label-eyebrow">{section.title}</span>
              </div>
              <ul className="space-y-0.5">
                {section.items.map((it) => (
                  <li key={it.to}>
                    <NavLink
                      end={it.to === "/"}
                      to={it.to}
                      className={({ isActive }) =>
                        [
                          "group flex items-start gap-3 px-2.5 py-2 rounded-md transition",
                          isActive
                            ? "bg-el-50 text-el-800 ring-1 ring-el-100"
                            : "text-slate-600 hover:bg-slate-50",
                        ].join(" ")
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <it.icon
                            className={[
                              "mt-0.5 h-4 w-4 shrink-0",
                              isActive ? "text-el-600" : "text-slate-400 group-hover:text-slate-500",
                            ].join(" ")}
                          />
                          <div className="leading-tight">
                            <div className="text-sm font-medium">{it.label}</div>
                            {it.hint && (
                              <div className={[
                                "text-[0.7rem]",
                                isActive ? "text-el-700/80" : "text-slate-400 group-hover:text-slate-500",
                              ].join(" ")}>
                                {it.hint}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* User profile entry — Auth0 aware */}
        <SidebarUserEntry />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 bg-white px-4 lg:px-6 flex items-center gap-2 lg:gap-4">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="p-2 -ml-1 rounded-md text-slate-600 hover:bg-slate-100 lg:hidden shrink-0"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-[1.05rem] font-semibold text-slate-900 leading-tight tracking-tightish truncate">
              {head.title}
            </h1>
            {head.sub && <div className="text-xs text-slate-500 truncate">{head.sub}</div>}
          </div>

          <div className="flex-1" />

          {/* Org switcher (topbar) — hidden in single-org release 1.
              Flip NEXT_FEATURES to bring back the multi-org workflow. */}
          {NEXT_FEATURES && (
            <OrgSwitcher
              current={current}
              memberships={myMemberships}
              onSelect={setOrgId}
            />
          )}

          <div className="relative w-60 max-w-full hidden lg:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              placeholder="Search sessions, sports…"
              className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-slate-50 border border-slate-200 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:shadow-ring"
            />
          </div>

          <button className="btn-secondary !h-9 hidden sm:inline-flex">
            <Bell className="h-4 w-4" />
          </button>

          <Link to="/upload" className="btn-primary !h-9 shrink-0">
            <UploadIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Upload</span>
          </Link>

          <HeaderProfileButton />
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-4 lg:py-6">
            <Outlet
              context={{
                org: currentOrg,
                currentOrgId: currentOrg.id,
                role,
                setOrgId,
                memberships: myMemberships,
              }}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

function OrgSwitcher({
  current,
  memberships,
  onSelect,
}: {
  current: { org: Org; role: OrgRole };
  memberships: { org: Org; role: OrgRole }[];
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="h-9 inline-flex items-center gap-2 px-2.5 pr-3 rounded-md bg-slate-50 border border-slate-200 hover:bg-white hover:shadow-surface transition"
      >
        <OrgIcon org={current.org} small />
        <div className="leading-tight text-left">
          <div className="text-xs font-semibold text-slate-800 leading-none">{current.org.name}</div>
          <div className="text-[0.65rem] text-slate-500 mt-0.5">
            {ROLE_LABEL[current.role]} · {planLabel(current.org.plan)}
          </div>
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 right-0 mt-2 w-72 rounded-md bg-white border border-slate-200 shadow-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="label-eyebrow">Switch organisation</div>
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {memberships.map((m) => (
              <li key={m.org.id}>
                <button
                  onClick={() => { onSelect(m.org.id); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 text-left"
                >
                  <OrgIcon org={m.org} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm font-medium text-slate-800 truncate">{m.org.name}</div>
                      {m.org.id === GLOBAL_ORG_ID && (
                        <span className="text-[0.6rem] font-semibold uppercase tracking-[0.06em] text-slate-500">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-[0.7rem] text-slate-400 truncate">
                      {ROLE_LABEL[m.role]} · {planLabel(m.org.plan)} · {m.org.type}
                    </div>
                  </div>
                  {m.org.id === current.org.id && <Check className="h-3.5 w-3.5 text-el-600 shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-slate-100 p-2">
            <button className="w-full flex items-center gap-2 text-xs font-medium text-el-700 hover:text-el-800 px-2 py-1.5 rounded hover:bg-el-50">
              <Plus className="h-3.5 w-3.5" />
              Create organisation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrgIcon({ org, small = false }: { org: Org; small?: boolean }) {
  const isGlobal = org.id === GLOBAL_ORG_ID;
  const Icon = isGlobal ? Globe2 : Building2;
  const size = small ? "h-6 w-6" : "h-8 w-8";
  const iconSize = small ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <div
      className={[
        size,
        "rounded-md flex items-center justify-center shrink-0",
        isGlobal
          ? "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
          : "bg-gradient-to-br from-el-400 to-el-700 text-white",
      ].join(" ")}
    >
      <Icon className={iconSize} />
    </div>
  );
}


/* ---------- Sidebar user entry (Auth0-aware) ---------- */

function SidebarUserEntry() {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout } = useAuth0();

  if (isLoading) {
    return (
      <div className="border-t border-slate-200 p-3">
        <div className="h-12 rounded-md bg-slate-50 animate-pulse" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="border-t border-slate-200 p-3">
        <button
          type="button"
          onClick={() => loginWithRedirect()}
          className="w-full flex items-center justify-center gap-2 px-2.5 py-2 rounded-md transition btn-primary !h-9"
        >
          Log in
        </button>
      </div>
    );
  }

  const displayName = user?.name ?? user?.email ?? ME.name;
  const initials =
    user?.name
      ?.split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ?? ME.initials;

  return (
    <div className="border-t border-slate-200 p-3 space-y-1.5">
      <NavLink
        to="/profile"
        className={({ isActive }) =>
          [
            "w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md transition",
            isActive ? "bg-el-50 ring-1 ring-el-100" : "hover:bg-slate-50",
          ].join(" ")
        }
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={displayName}
              className="h-8 w-8 rounded-full shrink-0 object-cover"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-el-400 to-el-700 text-white text-[0.7rem] font-semibold flex items-center justify-center shrink-0">
              {initials}
            </div>
          )}
          <div className="leading-tight text-left min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">{displayName}</div>
            <div className="text-[0.7rem] text-slate-400 truncate">View profile</div>
          </div>
        </div>
      </NavLink>
      <button
        type="button"
        onClick={() =>
          logout({ logoutParams: { returnTo: window.location.origin } })
        }
        className="w-full text-left text-xs text-slate-500 hover:text-slate-800 px-2.5 py-1 rounded-md hover:bg-slate-50"
      >
        Log out
      </button>
    </div>
  );
}


/* ---------- Header profile button (avatar + menu) ---------- */

function HeaderProfileButton() {
  const { isAuthenticated, user, logout } = useAuth0();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!isAuthenticated) return null;

  const displayName = user?.name ?? ME.name;
  const initials =
    user?.name
      ?.split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ?? ME.initials;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-9 w-9 rounded-full overflow-hidden ring-1 ring-slate-200 hover:ring-el-300 transition flex items-center justify-center"
        aria-label="Account menu"
      >
        {user?.picture ? (
          <img src={user.picture} alt={displayName} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-el-400 to-el-700 text-white text-[0.7rem] font-semibold flex items-center justify-center">
            {initials}
          </div>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-60 card p-2 shadow-lg z-50">
          <div className="px-2 py-2 border-b border-slate-100 mb-1">
            <div className="text-sm font-semibold text-slate-900 truncate">{displayName}</div>
            {user?.email && (
              <div className="text-[0.7rem] text-slate-500 truncate">{user.email}</div>
            )}
          </div>
          <NavLink
            to="/profile"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              [
                "flex items-center gap-2 px-2 py-2 rounded-md text-sm transition",
                isActive ? "bg-el-50 text-el-800" : "text-slate-700 hover:bg-slate-50",
              ].join(" ")
            }
          >
            <SettingsIcon className="h-4 w-4" />
            Profile &amp; settings
          </NavLink>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              logout({ logoutParams: { returnTo: window.location.origin } });
            }}
            className="w-full text-left flex items-center gap-2 px-2 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-50"
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
